import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  downloadBloombergCsv,
  getBloombergCatalogo,
  getBloombergPrecios,
} from "@/lib/api";

const STORAGE_KEY = "portal-edocta:bloomberg-config";

// Paleta estable por ticker — mismo color sin importar el orden de selección.
const COLORS: Record<string, string> = {
  "PGFCHOSC Index": "#2563eb",
  "PGFCHHUB Index": "#dc2626",
  "PGFCTXIN Index": "#16a34a",
  "PGFCAGDU Index": "#d97706",
  "PGFCSCLP Index": "#7c3aed",
  "PGFCKTPT Index": "#0891b2",
  "LVGD0031 Index": "#db2777",
};

const PRECIO_FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

function fmtPrecio(v: string | number | null): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return PRECIO_FMT.format(n);
}

function fmtFecha(s: string): string {
  return new Date(s + "T00:00:00").toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

type StoredCfg = {
  tickers?: string[];
  desde?: string;
  hasta?: string;
};

export function BloombergPage() {
  const stored = useMemo<StoredCfg>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(stored.tickers ?? []),
  );
  const [desde, setDesde] = useState<string>(stored.desde ?? "");
  const [hasta, setHasta] = useState<string>(stored.hasta ?? "");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ tickers: [...selected], desde, hasta }),
      );
    } catch {
      // ignore
    }
  }, [selected, desde, hasta]);

  const catalogoQ = useQuery({
    queryKey: ["bloomberg:catalogo"],
    queryFn: getBloombergCatalogo,
    staleTime: 10 * 60_000,
  });

  // Default: seleccionar el primero del catálogo si no hay nada en storage,
  // y arrancar con el último año visible.
  useEffect(() => {
    if (!catalogoQ.data) return;
    if (selected.size === 0 && catalogoQ.data.items.length > 0) {
      setSelected(new Set([catalogoQ.data.items[0].ticker]));
    }
    if (!desde && catalogoQ.data.items.length > 0) {
      const maxs = catalogoQ.data.items
        .map((t) => t.fecha_max)
        .filter((d): d is string => !!d);
      if (maxs.length > 0) {
        const max = maxs.reduce((a, b) => (a > b ? a : b));
        const d = new Date(max);
        d.setFullYear(d.getFullYear() - 1);
        setDesde(d.toISOString().slice(0, 10));
        setHasta(max);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogoQ.data]);

  const preciosQ = useQuery({
    queryKey: ["bloomberg:precios", [...selected].sort().join(","), desde, hasta],
    queryFn: () =>
      getBloombergPrecios({
        tickers: [...selected],
        desde: desde || undefined,
        hasta: hasta || undefined,
      }),
    enabled: selected.size > 0,
  });

  const tickersList = catalogoQ.data?.items ?? [];

  // Reorganiza filas (fecha, ticker, px_last) en filas indexadas por fecha con
  // una columna por ticker — formato que LineChart consume directo.
  const chartData = useMemo(() => {
    if (!preciosQ.data) return [];
    const byFecha = new Map<string, Record<string, number | string>>();
    for (const r of preciosQ.data.items) {
      if (r.px_last == null) continue;
      const n = Number(r.px_last);
      if (!Number.isFinite(n)) continue;
      const row = byFecha.get(r.fecha);
      if (row) {
        row[r.ticker] = n;
      } else {
        byFecha.set(r.fecha, { fecha: r.fecha, [r.ticker]: n });
      }
    }
    return [...byFecha.values()].sort((a, b) =>
      String(a.fecha).localeCompare(String(b.fecha)),
    );
  }, [preciosQ.data]);

  const toggle = (ticker: string) => {
    const next = new Set(selected);
    if (next.has(ticker)) next.delete(ticker);
    else next.add(ticker);
    setSelected(next);
  };

  const handleDownload = async () => {
    if (selected.size === 0) return;
    setDownloading(true);
    try {
      await downloadBloombergCsv({
        tickers: [...selected],
        desde: desde || undefined,
        hasta: hasta || undefined,
      });
    } catch (err) {
      alert(`Error al descargar: ${(err as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  const fechaMinGlobal = tickersList.reduce<string | undefined>(
    (acc, t) => (t.fecha_min && (!acc || t.fecha_min < acc) ? t.fecha_min : acc),
    undefined,
  );
  const fechaMaxGlobal = tickersList.reduce<string | undefined>(
    (acc, t) => (t.fecha_max && (!acc || t.fecha_max > acc) ? t.fecha_max : acc),
    undefined,
  );

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Desde">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={desde}
              min={fechaMinGlobal}
              max={fechaMaxGlobal}
              onChange={(e) => setDesde(e.target.value)}
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={hasta}
              min={fechaMinGlobal}
              max={fechaMaxGlobal}
              onChange={(e) => setHasta(e.target.value)}
            />
          </Field>
          <div className="md:col-span-2 flex items-end gap-2">
            <button
              type="button"
              disabled={selected.size === 0 || downloading}
              onClick={handleDownload}
              className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Descargar histórico filtrado en CSV"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Descargando…" : "CSV"}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!fechaMinGlobal || !fechaMaxGlobal) return;
                setDesde(fechaMinGlobal);
                setHasta(fechaMaxGlobal);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Todo el histórico
            </button>
            <button
              type="button"
              onClick={() => {
                if (!fechaMaxGlobal) return;
                const d = new Date(fechaMaxGlobal);
                d.setFullYear(d.getFullYear() - 1);
                setDesde(d.toISOString().slice(0, 10));
                setHasta(fechaMaxGlobal);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Último año
            </button>
          </div>
        </div>

        <div className="pt-2 border-t">
          <div className="text-xs font-medium text-slate-600 mb-1.5">Tickers</div>
          {catalogoQ.isLoading && (
            <div className="text-xs text-slate-500">Cargando catálogo…</div>
          )}
          {catalogoQ.isError && (
            <div className="text-xs text-red-600">
              Error: {(catalogoQ.error as Error).message}
            </div>
          )}
          {catalogoQ.data && (
            <div className="flex flex-wrap gap-2">
              {tickersList.map((t) => {
                const isOn = selected.has(t.ticker);
                const color = COLORS[t.ticker] ?? "#64748b";
                return (
                  <button
                    key={t.ticker}
                    type="button"
                    onClick={() => toggle(t.ticker)}
                    className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border transition ${
                      isOn
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                    title={t.ticker}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <span className="font-medium">{t.alias ?? t.ticker}</span>
                    <span
                      className={`font-mono text-[10px] ${
                        isOn ? "text-slate-300" : "text-slate-400"
                      }`}
                    >
                      {t.ticker.replace(" Index", "")}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {selected.size === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">
            Selecciona al menos un ticker.
          </div>
        )}
        {selected.size > 0 && preciosQ.isLoading && (
          <div className="p-8 text-center text-slate-500 text-sm">Cargando…</div>
        )}
        {preciosQ.isError && (
          <div className="p-8 text-center text-red-600 text-sm">
            Error: {(preciosQ.error as Error).message}
          </div>
        )}
        {selected.size > 0 && preciosQ.data && chartData.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">
            Sin datos en el rango seleccionado.
          </div>
        )}
        {chartData.length > 0 && (
          <div className="p-4">
            <div className="h-[480px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 24, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="fecha"
                    tickFormatter={fmtFecha}
                    minTickGap={32}
                    tick={{ fontSize: 11, fill: "#475569" }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#475569" }}
                    tickFormatter={(v) => PRECIO_FMT.format(Number(v))}
                    width={72}
                  />
                  <Tooltip
                    labelFormatter={(l) => fmtFecha(String(l))}
                    formatter={(v: number | string, name: string) => {
                      const alias =
                        tickersList.find((t) => t.ticker === name)?.alias ?? name;
                      return [fmtPrecio(v as number), alias];
                    }}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    formatter={(value) => {
                      const alias =
                        tickersList.find((t) => t.ticker === value)?.alias ?? value;
                      return `${alias}`;
                    }}
                  />
                  {[...selected].map((ticker) => (
                    <Line
                      key={ticker}
                      type="monotone"
                      dataKey={ticker}
                      stroke={COLORS[ticker] ?? "#64748b"}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-xs text-slate-500 pt-2 mt-2 border-t flex flex-wrap gap-x-4 gap-y-1">
              <span>{chartData.length.toLocaleString("es-MX")} días</span>
              <span>
                Rango: {fmtFecha(String(chartData[0].fecha))} →{" "}
                {fmtFecha(String(chartData[chartData.length - 1].fecha))}
              </span>
              <span>{selected.size} ticker(s) graficado(s)</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
